import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "./utils/api";
import Select from "react-select";
import { checkboxMultiProps } from "./utils/MultiSelectCheckbox";
import { FaTimes } from "react-icons/fa";
import logo_eagle from "./assets/img/logo_eagle.png";
import "./dSpr.css";
import "./Leave_dashboard.css";

const LeaveDashboard = ({ navSize }) => {
    const [requestsToApprove, setRequestsToApprove] = useState(0);
    const [approvedThisMonth, setApprovedThisMonth] = useState(0);
    const [rejectedThisMonth, setRejectedThisMonth] = useState(0);
    const [onLeaveToday, setOnLeaveToday] = useState([]);

    const [employeesLeaves, setEmployeesLeaves] = useState([]);
    const [holidaysThisMonth, setHolidaysThisMonth] = useState([]);
    const [nextHoliday, setNextHoliday] = useState(null);

    const now = new Date();
    const month = now.getMonth() + 1;
    const year = now.getFullYear();

    const [departmentList, setDepartmentList] = useState([]);
    const [deptFilter, setDeptFilter] = useState([]);

    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    const [showAll, setShowAll] = useState(false);

    const [showModal, setShowModal] = useState(false);
    const [modalTitle, setModalTitle] = useState("");
    const [modalData, setModalData] = useState([]);
    const [allLeaves, setAllLeaves] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        fetchCounts();
        fetchHolidays();
        fetchDepartments();
        fetchloginedEmployees();
    }, []);

    const fetchloginedEmployees = async () => {
        try {
            const res = await apiFetch(`/api/employee/logined_employee`, {
                method: 'GET',
                credentials: 'include',
            });
            const data = await res.json();

            if (data?.message === "Access Denied. No Token Provided!" ||
                data?.message === "Invalid Token") {
                navigate("/login");
                return;
            }

            // Role redirect removed - ProtectedRoute (App.js) gates via matrix.

        } catch (err) {
            console.error("Error fetching employees:", err);
        }
    };

    const fetchDepartments = async () => {
        const res = await apiFetch(`/api/Employee_configuration/get-department-order`, {
            method: 'GET',
            credentials: 'include',
        });
        const data = await res.json();
        setDepartmentList(data.departments || []);
    };

    const fetchCounts = async () => {
        const res = await apiFetch("/api/leave/leave_requests_list");
        const raw = await res.json();
        // Guard against the backend returning { error: "..." } on DB failures.
        const data = Array.isArray(raw) ? raw : [];
        setAllLeaves(data);
        setRequestsToApprove(data.filter(l => l.leave_status === "Pending").length);
        setApprovedThisMonth(
            data.filter(
                l =>
                    l.leave_status === "Approved" &&
                    new Date(l.created_time).getMonth() + 1 === month
            ).length
        );
        setRejectedThisMonth(
            data.filter(
                l =>
                    l.leave_status === "Rejected" &&
                    new Date(l.created_time).getMonth() + 1 === month
            ).length
        );

        setEmployeesLeaves(
            data.filter(
                l =>
                    new Date(l.start_date).getMonth() + 1 === month ||
                    new Date(l.end_date).getMonth() + 1 === month
            )
        );

        // On Leave Today
        const today = new Date().toISOString().split("T")[0];
        setOnLeaveToday(
            data.filter(l => today >= l.start_date && today <= l.end_date)
        );
    };

    const fetchHolidays = async () => {
        const res = await apiFetch(`/api/holiday/list?month=${month}&year=${year}`);
        const result = await res.json();
        setHolidaysThisMonth(result);

        // Find the next upcoming holiday
        const today = new Date();
        const upcomingDate = result.find(h => new Date(h.holiday_date) >= today)?.holiday_date;

        const upcomingHolidays = upcomingDate
            ? result.filter(h => h.holiday_date === upcomingDate)
            : [];

        setNextHoliday(upcomingHolidays);
    };

    // Open modal with filtered leave list
    const openModal = (type) => {
        let filtered = [];

        if (type === "Requests to Approve") {
            filtered = allLeaves.filter(l => l.leave_status === "Pending");
        }

        if (type === "Approved Leaves In This Month") {
            filtered = allLeaves.filter(
                l =>
                    l.leave_status === "Approved" &&
                    new Date(l.created_time).getMonth() + 1 === month
            );
        }

        if (type === "Rejected Leaves In This Month") {
            filtered = allLeaves.filter(
                l =>
                    l.leave_status === "Rejected" &&
                    new Date(l.created_time).getMonth() + 1 === month
            );
        }

        setModalTitle(type);
        setModalData(filtered);
        setShowModal(true);
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric"
        });
    };
    // Date + breakdown label. Stacked ("09 Jul 2026" on one line, "(First half)"
    // below) so the Start/End Date columns stay aligned instead of wrapping the
    // whole thing mid-word.
    const formatDateBreakdown = (date, breakdown) => {
        if (!date) return <span>-</span>;
        const base = formatDate(date);
        const b = (breakdown || "").toLowerCase();
        let label = "";
        if (b === "first_half"  || b === "first half"  || b === "1st_half") label = "First half";
        else if (b === "second_half" || b === "second half" || b === "2nd_half") label = "Second half";
        else if (b === "full" || b === "full_day" || !b) label = "Full";
        else label = breakdown;
        return (
            <>
                <div>{base}</div>
                <div className="ld-breakdown-sub">({label})</div>
            </>
        );
    };
    // Department name lookup for On Leave rows.
    const deptName = (deptId) =>
        departmentList.find(d => String(d.department_id) === String(deptId))?.department_name || "";
    // Compact date-range for holidays. Single day shows once; multi-day shows range.
    const formatHolidayDateRange = (start, end) => {
        if (!start) return "";
        const s = formatDate(start);
        if (!end || normalizeDate(end) === normalizeDate(start)) return s;
        return `${s} - ${formatDate(end)}`;
    };
    const statusColor = (status) =>
        status === "Approved" ? "#16a34a" :
        status === "Rejected" ? "#dc2626" :
        status === "Canceled" ? "#64748b" :
                                "#2563eb"; // Pending / other
    const normalizeDate = (d) => {
        if (!d) return "";
        return new Date(d).toISOString().split("T")[0];
    };

    return (
        <div className="leave-dashboard-container">
            <div className={`leave-dashboard ${navSize}`}>
                <div className="dspr-header-wrapper">
                    <h2>
                        <a className="navbar-brand" href="#">
                            <img src={logo_eagle} alt="dAdmin Logo" style={{ height: "50px", objectFit: "contain", marginLeft: "-20px" }} />
                        </a>
                        Leave Dashboard
                    </h2>
                    <p className="dspr-subtitle">
                        Live snapshot of leave activity for the current month - pending requests, decisions
                        taken, who's out today, and what's coming up on the holiday calendar.
                    </p>
                </div>
                <div className="dashboard-grid">

                    {/* Requests */}
                    <div
                        className="card requests hover-grow"
                        onClick={() => openModal("Requests to Approve")}
                    >
                        <h4>Requests to Approve</h4>
                        <span className="count">{requestsToApprove}</span>
                    </div>

                    {/* Approved */}
                    <div
                        className="card approved hover-grow"
                        onClick={() => openModal("Approved Leaves In This Month")}
                    >
                        <h4>Approved Leaves In This Month</h4>
                        <span className="count">{approvedThisMonth}</span>
                    </div>

                    {/* Rejected */}
                    <div
                        className="card rejected hover-grow"
                        onClick={() => openModal("Rejected Leaves In This Month")}
                    >
                        <h4>Rejected Leaves In This Month</h4>
                        <span className="count">{rejectedThisMonth}</span>
                    </div>

                    {showModal && (
                        <div className="leave-dashboard-modal-overlay">
                            <div className="leave-dashboard-modal-box">
                                <div className="leave-dashboard-modal-header">
                                    <h2>{modalTitle}</h2>
                                    <FaTimes className="close-btn" onClick={() => setShowModal(false)} />
                                </div>

                                <table className="leave-dashboard-modal-table">
                                    <thead>
                                        <tr>
                                            <th>Leave ID</th>
                                            <th>Employee</th>
                                            <th>Leave Type</th>
                                            <th>Start Date</th>
                                            <th>End Date</th>
                                            <th>Days</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {modalData.length === 0 ? (
                                            <tr>
                                                <td colSpan="7" className="no-data">No Records Found</td>
                                            </tr>
                                        ) : (
                                            modalData.map(l => (
                                                <tr key={l.leave_requests_id}>
                                                    <td>{l.leave_requests_id}</td>
                                                    <td className="ld-emp-cell">
                                                        <div>{l.emp_name}</div>
                                                        <span className="ld-emp-id-chip">{l.emp_id}</span>
                                                    </td>
                                                    <td className="ld-nowrap">{l.leave_type}</td>
                                                    <td>{formatDateBreakdown(l.start_date, l.start_date_breakdown)}</td>
                                                    <td>{formatDateBreakdown(l.end_date,   l.end_date_breakdown)}</td>
                                                    <td>{l.no_of_days}</td>
                                                    <td style={{ fontWeight: 600, color: statusColor(l.leave_status) }}>
                                                        {l.leave_status}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* holiday Dashboard */}
                    <div className="holiday-card">
                        {/* Next Holidays */}
                        <div className="next-holiday">
                            <button className="holiday-btn">
                                <h2>Next Holiday </h2>
                            </button>

                            <div className="holiday-box">
                                {nextHoliday && nextHoliday.length > 0 ? (
                                    <>
                                        {nextHoliday.slice(0, 2).map((h, index) => (
                                            <div key={index} className="next-holiday-name">
                                                <div>{h.holiday_name}</div>
                                                <div className="next-holiday-date">
                                                    {formatHolidayDateRange(h.holiday_date, h.holiday_end)}
                                                </div>
                                            </div>
                                        ))}

                                        {nextHoliday.length > 2 && (
                                            <div className="more-box"
                                                onMouseEnter={() => setShowAll(true)}
                                                onMouseLeave={() => setShowAll(false)}
                                            >
                                                +{nextHoliday.length - 2} more
                                                {showAll && (
                                                    <div className="holiday-more-popup">
                                                        {nextHoliday.map((h, i) => (
                                                            <div key={i} className="popup-item">
                                                                <b>{h.holiday_name}</b>
                                                                <div>{formatDate(h.holiday_date)} to {formatDate(h.holiday_end)}</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="no-holiday">No Upcoming Holiday</div>
                                )}
                            </div>
                        </div>

                        {/* Holidays This Month Section */}
                        <div className="this-month-section">
                            <h2 style={{ marginTop: "15px" }}>Holidays This Month</h2>

                            {holidaysThisMonth.length === 0 ? (
                                <div className="empty-box">
                                    No more holidays scheduled for this month.
                                </div>
                            ) : (
                                holidaysThisMonth.map(h => (
                                    <div key={h.holiday_id} className="holiday-item">
                                        <div className="holiday-title">{h.holiday_name}</div>

                                        <div className="holiday-date">
                                            {formatDate(h.holiday_date)} to {formatDate(h.holiday_end)}
                                        </div>

                                        <div className="holiday-for">
                                            {h.holiday_for === "employee"
                                                ? "For: All Employees"
                                                : h.holiday_for === "department"
                                                    ? `For Department: ${h.department_name}`
                                                    : h.holiday_for === "job"
                                                        ? `For Job: ${h.job_name}`
                                                        : ""}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* On Leave Today */}
                    <div className="on-leave card">
                        <div className="card-header-row">
                            <h3>On Leave</h3>

                            <div style={{ width: "180px", marginTop: "-10px" }}>
                                <Select
                                    placeholder="Department"
                                    options={departmentList.map(d => ({
                                        value: d.department_id,
                                        label: d.department_name,
                                    }))}
                                    value={deptFilter.map(id => {
                                        const d = departmentList.find(dep => dep.department_id === id);
                                        return { value: d?.department_id, label: d?.department_name };
                                    })}
                                    onChange={selected => {
                                        const ids = selected ? selected.map(s => s.value) : [];
                                        setDeptFilter(ids);
                                    }}
                                    isMulti
                                    {...checkboxMultiProps}
                                    closeMenuOnSelect={false}
                                    isClearable
                                />
                            </div>
                        </div>
                        {onLeaveToday.length === 0 ? (
                            <div className="empty-box">
                                <p>No employees have taken leave today.</p>
                            </div>
                        ) : (
                            onLeaveToday
                                .filter(e =>
                                    deptFilter.length === 0 ||
                                    deptFilter.includes(Number(e.emp_department))
                                )
                                .map(e => (
                                    <div key={e.leave_requests_id} className="ld-onleave-row">
                                        <div className="ld-onleave-name">{e.emp_name}</div>
                                        <div className="ld-onleave-meta">
                                            <span className="ld-emp-id-chip">{e.emp_id}</span>
                                            {deptName(e.emp_department) && (
                                                <span className="ld-dept-chip">{deptName(e.emp_department)}</span>
                                            )}
                                        </div>
                                    </div>
                                ))
                        )}
                    </div>

                    {/* Employee Leaves Table */}
                    <div className="employee-leaves card">
                        <div className="card-header-row">
                            <h3>Employee Leaves</h3>

                            <div className="date-filter-wrapper" style={{ position: "relative" }}>
                                <div className="date-range" style={{ display: "flex", gap: "8px", marginTop: "-10px" }}>
                                    <input
                                        type="date"
                                        value={fromDate}
                                        onChange={(e) => setFromDate(e.target.value)}
                                        className="date-filter"
                                        style={{ width: "130px", padding: "5px" }}
                                        onClick={(e) => e.target.showPicker?.()}
                                        placeholder="From"
                                    />

                                    <input
                                        type="date"
                                        value={toDate}
                                        onChange={(e) => setToDate(e.target.value)}
                                        className="date-filter"
                                        style={{ width: "130px", padding: "5px" }}
                                        onClick={(e) => e.target.showPicker?.()}
                                        placeholder="To"
                                    />

                                    {(fromDate || toDate) && (
                                        <span
                                            onClick={() => { setFromDate(""); setToDate(""); }}
                                            style={{
                                                cursor: "pointer",
                                                fontSize: "18px",
                                                fontWeight: "bold",
                                                color: "#555",
                                                lineHeight: "30px"
                                            }}
                                        >
                                            <FaTimes
                                                size={18}
                                                style={{ cursor: "pointer" }}
                                                title="Clear Date Range"
                                            />
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="table-section">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Employee</th>
                                        <th>Leave Type</th>
                                        <th>Start Date</th>
                                        <th>End Date</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {employeesLeaves.length === 0 ? (
                                        <tr>
                                            <td colSpan={5} className="no-data">No data available</td>
                                        </tr>
                                    ) : (
                                        employeesLeaves
                                            .filter(e => {
                                                const start = normalizeDate(e.start_date);
                                                const end = normalizeDate(e.end_date);

                                                if (fromDate && !toDate) {
                                                    return start >= fromDate || end >= fromDate;
                                                }
                                                if (!fromDate && toDate) {
                                                    return start <= toDate || end <= toDate;
                                                }
                                                if (fromDate && toDate) {
                                                    return (
                                                        (start >= fromDate && start <= toDate) ||
                                                        (end >= fromDate && end <= toDate) ||
                                                        (fromDate >= start && toDate <= end) // overlapping
                                                    );
                                                }
                                                return true;
                                            })
                                            .map(e => (
                                                <tr key={e.leave_requests_id}>
                                                    <td className="ld-emp-cell">
                                                        <div>{e.emp_name}</div>
                                                        <span className="ld-emp-id-chip">{e.emp_id}</span>
                                                    </td>
                                                    <td className="ld-nowrap">{e.leave_type}</td>
                                                    <td>{formatDateBreakdown(e.start_date, e.start_date_breakdown)}</td>
                                                    <td>{formatDateBreakdown(e.end_date,   e.end_date_breakdown)}</td>
                                                    <td style={{ fontWeight: 600, color: statusColor(e.leave_status) }}>
                                                        {e.leave_status}
                                                    </td>
                                                </tr>
                                            ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                </div>
            </div >
        </div>
    );
};

export default LeaveDashboard;
